import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Play, Users, Trophy, Plus, Target, BarChart3, Eye, MapPin, Shield, X, Settings, Hash } from 'lucide-react';
import { collection, onSnapshot, query, orderBy, where, doc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import FixtureManager from '../components/FixtureManager';
import TeamPairings from '../components/TeamPairings';
import MatchSetup from '../components/MatchSetup';

const FixturesPage = () => {
  const { isScoreKeeper } = useAuth();
  const [fixtures, setFixtures] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showPlayerSelection, setShowPlayerSelection] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [selectedPlayers, setSelectedPlayers] = useState({
    home: { player1: null, player2: null },
    away: { player1: null, player2: null }
  });
  const [showFixtureManager, setShowFixtureManager] = useState(false);
  const [managerFixture, setManagerFixture] = useState(null);
  const [showTeamPairings, setShowTeamPairings] = useState(false);
  const [pairingsFixture, setPairingsFixture] = useState(null);
  const [showMatchSetup, setShowMatchSetup] = useState(false);
  const [setupFixture, setSetupFixture] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleGroup, setRescheduleGroup] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newRound, setNewRound] = useState(1);

  // Date filtering functions
  const getDateFilters = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfWeek.getDate() - 7);
    const endOfLastWeek = new Date(startOfLastWeek);
    endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);

    return {
      today: today.toISOString().split('T')[0],
      thisWeek: { start: startOfWeek.toISOString().split('T')[0], end: endOfWeek.toISOString().split('T')[0] },
      lastWeek: { start: startOfLastWeek.toISOString().split('T')[0], end: endOfLastWeek.toISOString().split('T')[0] }
    };
  };

  const filterFixturesByDate = (fixtures) => {
    if (!fixtures || !Array.isArray(fixtures)) return [];
    if (dateFilter === 'all') return fixtures;
    
    try {
      const dateFilters = getDateFilters();
      
      return fixtures.filter(fixture => {
        if (!fixture || !fixture.date) return false;
        const fixtureDate = fixture.date;
        
        switch (dateFilter) {
          case 'today':
            return fixtureDate === dateFilters.today;
          case 'thisWeek':
            return fixtureDate >= dateFilters.thisWeek.start && fixtureDate <= dateFilters.thisWeek.end;
          case 'lastWeek':
            return fixtureDate >= dateFilters.lastWeek.start && fixtureDate <= dateFilters.lastWeek.end;
          case 'custom':
            if (customDateRange.start && customDateRange.end) {
              return fixtureDate >= customDateRange.start && fixtureDate <= customDateRange.end;
            }
            return true;
          default:
            return true;
        }
      });
    } catch (error) {
      console.error('Error filtering fixtures by date:', error);
      return fixtures;
    }
  };

  // Function to group fixtures by team pairing
  const groupFixturesByTeamPairing = (fixtures) => {
    if (!fixtures || !Array.isArray(fixtures)) return [];
    
    try {
      const groups = {};
      
      fixtures.forEach(fixture => {
        if (!fixture) return;
        
        // Allow TBD playoff fixtures (Q2 and Finals) to be displayed
        if (!fixture.isPlayoff && (!fixture.homeTeamId || !fixture.awayTeamId)) return;
        
        // Use playoff round as key for TBD fixtures, otherwise use team IDs
        const key = fixture.isPlayoff && (!fixture.homeTeamId || !fixture.awayTeamId)
          ? `playoff-${fixture.playoffRound}`
          : `${fixture.homeTeamId}-${fixture.awayTeamId}`;
          
        if (!groups[key]) {
          groups[key] = {
            homeTeam: fixture.homeTeam,
            awayTeam: fixture.awayTeam,
            homeTeamId: fixture.homeTeamId,
            awayTeamId: fixture.awayTeamId,
            round: fixture.round,
            date: fixture.date,
            court: fixture.court,
            isPlayoff: fixture.isPlayoff,
            playoffRound: fixture.playoffRound,
            matches: []
          };
        }
        groups[key].matches.push(fixture);
      });
      
      return Object.values(groups).sort((a, b) => {
        if (a.round !== b.round) return a.round - b.round;
        return a.homeTeam?.localeCompare(b.homeTeam) || 0;
      });
    } catch (error) {
      console.error('Error grouping fixtures:', error);
      return [];
    }
  };

  useEffect(() => {
    let unsubscribeFixtures = null;
    let unsubscribeTeams = null;

    const setupListeners = () => {
      try {
        // Fetch all fixtures and filter in JavaScript to avoid index requirements
        const fixturesQuery = query(collection(db, 'fixtures'));

        unsubscribeFixtures = onSnapshot(fixturesQuery, (snapshot) => {
          try {
            let fixturesData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Filter in JavaScript based on selected filter
            if (filter === 'upcoming') {
              fixturesData = fixturesData.filter(f => f.status === 'upcoming' || f.status === 'scheduled');
            } else if (filter === 'live') {
              fixturesData = fixturesData.filter(f => f.status === 'live');
            } else if (filter === 'completed') {
              fixturesData = fixturesData.filter(f => f.status === 'completed');
            }
            
            // Apply date filtering
            fixturesData = filterFixturesByDate(fixturesData);
            
            // Sort in JavaScript
            fixturesData.sort((a, b) => {
              if (!a.date || !b.date) return 0;
              if (filter === 'completed') {
                return new Date(b.date) - new Date(a.date);
              }
              return new Date(a.date) - new Date(b.date);
            });
            
            setFixtures(fixturesData);
            setLoading(false);
          } catch (error) {
            console.error('Error processing fixtures data:', error);
            setLoading(false);
          }
        }, (error) => {
          console.error('Error fetching fixtures:', error);
          setLoading(false);
        });

        // Fetch teams for logos
        unsubscribeTeams = onSnapshot(collection(db, 'teams'), (snapshot) => {
          try {
            const teamsData = {};
            snapshot.docs.forEach(doc => {
              teamsData[doc.id] = doc.data();
            });
            setTeams(teamsData);
          } catch (error) {
            console.error('Error processing teams data:', error);
          }
        }, (error) => {
          console.error('Error fetching teams:', error);
        });
      } catch (error) {
        console.error('Error setting up listeners:', error);
        setLoading(false);
      }
    };

    setupListeners();

    return () => {
      if (unsubscribeFixtures) unsubscribeFixtures();
      if (unsubscribeTeams) unsubscribeTeams();
    };
  }, [filter, dateFilter, customDateRange]);

  const openPlayerSelection = (fixture) => {
    console.log('Opening player selection for fixture:', fixture);
    setSelectedFixture(fixture);
    setSelectedPlayers({
      home: { player1: null, player2: null },
      away: { player1: null, player2: null }
    });
    setShowPlayerSelection(true);
  };

  const startMatchWithPreSetPairings = async (fixture) => {
    console.log('Starting match with pre-set pairings for fixture:', fixture);
    if (!fixture.selectedPlayers) {
      alert('Please set team pairings first');
      return;
    }

    // Open match setup modal first
    handleMatchSetup(fixture);
  };

  const openRescheduleModal = (group) => {
    setRescheduleGroup(group);
    setNewDate(group.date);
    setNewRound(group.round || 1);
    setShowRescheduleModal(true);
  };

  const handleRescheduleGroup = async () => {
    if (!rescheduleGroup || !newDate) {
      alert('Please select a date');
      return;
    }

    try {
      // Update all fixtures in the group
      const updatePromises = rescheduleGroup.matches.map(async (fixture) => {
        const fixtureRef = doc(db, 'fixtures', fixture.id);
        await updateDoc(fixtureRef, {
          date: newDate,
          round: parseInt(newRound),
          updatedAt: new Date().toISOString()
        });
      });

      await Promise.all(updatePromises);
      
      alert(`Successfully rescheduled ${rescheduleGroup.matches.length} matches to ${new Date(newDate).toLocaleDateString()} - Round ${newRound}`);
      setShowRescheduleModal(false);
      setRescheduleGroup(null);
      setNewDate('');
      setNewRound(1);
    } catch (error) {
      console.error('Error rescheduling fixtures:', error);
      alert('Failed to reschedule fixtures. Please try again.');
    }
  };

  const completeMatchStart = async (fixture, setupData) => {
    try {
      console.log('Completing match start with setup:', setupData);
      
      const matchData = {
        status: 'live',
        startedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        currentSet: 1,
        court: setupData.court,
        matchNumber: setupData.matchNumber,
        sets: [
          { set: 1, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, completed: false },
          { set: 2, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, completed: false },
          { set: 3, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, completed: false }
        ],
        selectedPlayers: fixture.selectedPlayers,
        playerStats: {
          home: {
            player1: { 
              id: fixture.selectedPlayers.home.player1.id,
              name: fixture.selectedPlayers.home.player1.name,
              aces: 0, 
              unforcedErrors: 0, 
              kickSmashes: 0, 
              sportsmanshipRating: 5
            },
            player2: { 
              id: fixture.selectedPlayers.home.player2.id,
              name: fixture.selectedPlayers.home.player2.name,
              aces: 0, 
              unforcedErrors: 0, 
              kickSmashes: 0, 
              sportsmanshipRating: 5
            }
          },
          away: {
            player1: { 
              id: fixture.selectedPlayers.away.player1.id,
              name: fixture.selectedPlayers.away.player1.name,
              aces: 0, 
              unforcedErrors: 0, 
              kickSmashes: 0, 
              sportsmanshipRating: 5
            },
            player2: { 
              id: fixture.selectedPlayers.away.player2.id,
              name: fixture.selectedPlayers.away.player2.name,
              aces: 0, 
              unforcedErrors: 0, 
              kickSmashes: 0, 
              sportsmanshipRating: 5
            }
          }
        }
      };

      // Update fixture with match data
      await updateDoc(doc(db, 'fixtures', fixture.id), matchData);
      
      console.log('Match started successfully with setup');
    } catch (error) {
      console.error('Error starting match:', error);
      alert('Error starting match: ' + error.message);
    }
  };

  const startMatchWithPlayers = async () => {
    if (!selectedFixture) return;

    // Validate that all 4 players are selected
    if (!selectedPlayers.home.player1 || !selectedPlayers.home.player2 || 
        !selectedPlayers.away.player1 || !selectedPlayers.away.player2) {
      alert('Please select 2 players from each team');
      return;
    }

    try {
      const matchData = {
        status: 'live',
        startedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        currentSet: 1,
        sets: [
          { set: 1, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, completed: false },
          { set: 2, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, completed: false },
          { set: 3, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, completed: false }
        ],
        selectedPlayers: selectedPlayers,
        playerStats: {
          home: {
            player1: { 
              id: selectedPlayers.home.player1.id,
              name: selectedPlayers.home.player1.name,
              aces: 0, 
              unforcedErrors: 0, 
              kickSmashes: 0, 
              sportsmanshipRating: 5 
            },
            player2: { 
              id: selectedPlayers.home.player2.id,
              name: selectedPlayers.home.player2.name,
              aces: 0, 
              unforcedErrors: 0, 
              kickSmashes: 0, 
              sportsmanshipRating: 5 
            }
          },
          away: {
            player1: { 
              id: selectedPlayers.away.player1.id,
              name: selectedPlayers.away.player1.name,
              aces: 0, 
              unforcedErrors: 0, 
              kickSmashes: 0, 
              sportsmanshipRating: 5 
            },
            player2: { 
              id: selectedPlayers.away.player2.id,
              name: selectedPlayers.away.player2.name,
              aces: 0, 
              unforcedErrors: 0, 
              kickSmashes: 0, 
              sportsmanshipRating: 5 
            }
          }
        }
      };

      await updateDoc(doc(db, 'fixtures', selectedFixture.id), matchData);

      await addDoc(collection(db, 'matches'), {
        fixtureId: selectedFixture.id,
        leagueId: selectedFixture.leagueId,
        homeTeam: selectedFixture.homeTeam,
        homeTeamId: selectedFixture.homeTeamId,
        awayTeam: selectedFixture.awayTeam,
        awayTeamId: selectedFixture.awayTeamId,
        isPlayoff: !!selectedFixture.isPlayoff,
        playoffRound: selectedFixture.playoffRound || null,
        ...matchData,
        createdAt: new Date().toISOString()
      });

      setShowPlayerSelection(false);
      setSelectedFixture(null);
    } catch (error) {
      console.error('Error starting match:', error);
      alert('Error starting match: ' + error.message);
    }
  };

  const openFixtureManager = (fixture) => {
    setManagerFixture(fixture);
    setShowFixtureManager(true);
  };

  const handleSetPairings = (fixture) => {
    console.log('Setting pairings for fixture:', fixture);
    setPairingsFixture(fixture);
    setShowTeamPairings(true);
  };

  const handleSavePairings = async (pairings) => {
    try {
      console.log('Saving pairings for fixture:', pairingsFixture.id, pairings);
      
      // Update the fixture with the selected players
      await updateDoc(doc(db, 'fixtures', pairingsFixture.id), {
        selectedPlayers: pairings,
        updatedAt: new Date().toISOString()
      });
      
      console.log('Pairings saved successfully');
    } catch (error) {
      console.error('Error saving pairings:', error);
      throw error;
    }
  };

  const handleMatchSetup = (fixture) => {
    setSetupFixture(fixture);
    setShowMatchSetup(true);
  };

  const handleSaveMatchSetup = async (setupData) => {
    try {
      console.log('Saving match setup for fixture:', setupFixture.id, setupData);
      
      // Complete the match start with setup data
      await completeMatchStart(setupFixture, setupData);
      
      console.log('Match started successfully with setup');
    } catch (error) {
      console.error('Error saving match setup:', error);
      throw error;
    }
  };

  const closeFixtureManager = () => {
    setShowFixtureManager(false);
    setManagerFixture(null);
  };

  const handleFixtureUpdate = () => {
    // Refresh fixtures data
    console.log('Fixture updated, refreshing data...');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-8"
        >
          <div>
            <h1 className="text-4xl font-bold mb-2 text-green-600">
              Fixtures & Matches
            </h1>
            <p className="text-gray-600">
              View and manage match fixtures
            </p>
          </div>
          <Link
            to="/fixtures/generate"
            className="btn btn-primary flex items-center gap-2 shadow-lg"
          >
            <Plus size={20} />
            Generate Fixtures
          </Link>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', backgroundColor: 'white', borderRadius: '0.75rem', padding: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            {[
              { key: 'all', label: 'All Matches', icon: Calendar },
              { key: 'live', label: 'Live', icon: Play },
              { key: 'upcoming', label: 'Upcoming', icon: Clock },
              { key: 'completed', label: 'Completed', icon: Trophy }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                style={{
                  flex: 1,
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  transition: 'all 0.3s',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  minWidth: '120px',
                  background: filter === key ? 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)' : 'transparent',
                  color: filter === key ? 'white' : '#6B7280',
                  boxShadow: filter === key ? '0 4px 12px rgba(28, 86, 61, 0.3)' : 'none'
                }}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>

          {/* Date Filters */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', backgroundColor: 'white', borderRadius: '0.75rem', padding: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            {[
              { key: 'all', label: 'All Dates', icon: Calendar },
              { key: 'today', label: 'Today', icon: Clock },
              { key: 'thisWeek', label: 'This Week', icon: Calendar },
              { key: 'lastWeek', label: 'Last Week', icon: Calendar },
              { key: 'custom', label: 'Custom', icon: Settings }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setDateFilter(key)}
                style={{
                  flex: 1,
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  transition: 'all 0.3s',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  minWidth: '120px',
                  background: dateFilter === key ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' : 'transparent',
                  color: dateFilter === key ? 'white' : '#6B7280',
                  boxShadow: dateFilter === key ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none'
                }}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>

          {/* Custom Date Range */}
          {dateFilter === 'custom' && (
            <div style={{ display: 'flex', gap: '1rem', backgroundColor: 'white', borderRadius: '0.75rem', padding: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #E5E7EB',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #E5E7EB',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </div>
          )}
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <Trophy size={48} className="mx-auto mb-4 text-green-600 animate-bounce" />
              <p className="text-gray-600">Loading fixtures...</p>
          </div>
        </div>
        ) : fixtures.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {groupFixturesByTeamPairing(fixtures).map((group, groupIndex) => (
              <div key={`group-${groupIndex}`} style={{ marginBottom: '2rem' }}>
                {/* Team Pairing Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                  color: 'white',
                  padding: 'clamp(1rem, 2vw, 1.5rem)',
                  borderRadius: '1rem 1rem 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)'
                    }}>
                      <Trophy size={24} style={{ color: '#FCD34D' }} />
                    </div>
                    <div>
                      <h3 style={{ 
                        fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)', 
                        fontWeight: 'bold', 
                        margin: 0,
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                        letterSpacing: '0.5px',
                        color: 'white'
                      }}>
                        {group.homeTeam} vs {group.awayTeam}
                      </h3>
                      <p style={{ 
                        fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)', 
                        margin: '0.25rem 0 0 0',
                        color: 'rgba(255, 255, 255, 0.95)',
                        fontWeight: '500'
                      }}>
                        {group.matches.length} matches • {group.date} • {group.court}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.25)',
                      padding: '0.5rem 1rem',
                      borderRadius: '2rem',
                      fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)',
                      fontWeight: '700',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                    }}>
                      Round {group.round}
                    </div>
                    {!isScoreKeeper() && (
                      <button
                        onClick={() => openRescheduleModal(group)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: '0.75rem',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                          transition: 'all 0.2s ease',
                          backdropFilter: 'blur(10px)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <Calendar size={16} />
                        Reschedule
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Individual Matches */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                  {group.matches.map((fixture, matchIndex) => {
              const homeTeam = teams[fixture.homeTeamId];
              const awayTeam = teams[fixture.awayTeamId];
              
              const score = (fixture.status === 'live' || fixture.status === 'completed') && fixture.sets ? {
                home: fixture.sets.filter(s => s.completed && s.homeScore > s.awayScore).length,
                away: fixture.sets.filter(s => s.completed && s.awayScore > s.homeScore).length
              } : null;
            
            return (
              <motion.div
                key={fixture.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: matchIndex * 0.05 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '1rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s'
                  }}
                >
                  {/* Header with Status & Court */}
                  <div style={{
                    background: fixture.status === 'live' ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' :
                                fixture.status === 'completed' ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' :
                                'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    padding: '0.75rem 1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', fontWeight: '700', fontSize: '0.875rem' }}>
                      {fixture.status === 'live' && <div className="live-dot"></div>}
                      {fixture.status.toUpperCase()}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.875rem' }}>
                      <MapPin size={14} />
                      <span>{fixture.court || 'Court TBD'}</span>
                      {fixture.matchNumber && (
                        <>
                          <span style={{ margin: '0 0.25rem' }}>•</span>
                          <Hash size={14} />
                          <span>#{fixture.matchNumber}</span>
                        </>
                      )}
                      {fixture.isPlayoff && (
                        <>
                          <span style={{ margin: '0 0.25rem' }}>•</span>
                          <span style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '0.25rem',
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '9999px',
                            padding: '0.1rem 0.65rem',
                            fontWeight: '700'
                          }}>
                            Playoff · {fixture.playoffRound || 'TBD'}
                          </span>
                        </>
                      )}
                        </div>
                      </div>
                      
                  {/* Teams Display */}
                  <div style={{ padding: 'clamp(1rem, 2vw, 1.5rem)' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      gap: 'clamp(0.5rem, 1vw, 1rem)', 
                      marginBottom: '1rem'
                    }}>
                      {/* Home Team */}
                      <div style={{ 
                        flex: '1 1 0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: '0.75rem'
                      }}>
                        {homeTeam?.logo ? (
                          <img 
                            src={homeTeam.logo} 
                            alt={fixture.homeTeam} 
                            style={{
                              width: 'clamp(60px, 10vw, 80px)',
                              height: 'clamp(60px, 10vw, 80px)',
                              borderRadius: '12px',
                              objectFit: 'cover',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              border: '3px solid #10B981'
                            }}
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div style={{
                            width: 'clamp(60px, 10vw, 80px)',
                            height: 'clamp(60px, 10vw, 80px)',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #D1FAE5 0%, #10B981 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            border: '3px solid #10B981'
                          }}>
                            <Users size={32} style={{ color: '#059669' }} />
                          </div>
                        )}
                        <div style={{ width: '100%' }}>
                          <div style={{ 
                            fontSize: 'clamp(1rem, 2vw, 1.25rem)', 
                            fontWeight: 'bold', 
                            color: '#1F2937', 
                            marginBottom: '0.25rem',
                            lineHeight: 1.2
                          }}>
                            {fixture.homeTeam}
                          </div>
                          {score && (
                            <div style={{ 
                              fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', 
                              color: fixture.status === 'completed' ? '#059669' : '#10B981',
                              fontWeight: '600'
                            }}>
                              {fixture.status === 'completed' ? 'Final: ' : 'Sets: '}{score.home}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* VS / Score */}
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        minWidth: 'clamp(50px, 10vw, 70px)',
                        flexShrink: 0
                      }}>
                        {score ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <div style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', fontWeight: 'bold', color: '#10B981' }}>
                                {score.home}
                              </div>
                              <div style={{ fontSize: 'clamp(1rem, 2.5vw, 1.5rem)', color: '#9CA3AF' }}>-</div>
                              <div style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', fontWeight: 'bold', color: '#3B82F6' }}>
                                {score.away}
                              </div>
                            </div>
                            <div style={{ fontSize: 'clamp(0.625rem, 1.2vw, 0.75rem)', color: fixture.status === 'completed' ? '#059669' : '#9CA3AF', textTransform: 'uppercase', fontWeight: '600' }}>
                              {fixture.status === 'completed' ? 'FINAL' : 'Sets'}
                        </div>
                          </>
                        ) : (
                          <div style={{
                            padding: 'clamp(0.375rem, 1vw, 0.5rem) clamp(0.75rem, 2vw, 1rem)',
                            background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                            borderRadius: '9999px',
                            fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                            fontWeight: '700',
                            color: '#6B7280'
                          }}>
                            VS
                          </div>
                        )}
                      </div>

                      {/* Away Team */}
                      <div style={{ 
                        flex: '1 1 0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: '0.75rem'
                      }}>
                        {awayTeam?.logo ? (
                          <img 
                            src={awayTeam.logo} 
                            alt={fixture.awayTeam} 
                            style={{
                              width: 'clamp(60px, 10vw, 80px)',
                              height: 'clamp(60px, 10vw, 80px)',
                              borderRadius: '12px',
                              objectFit: 'cover',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              border: '3px solid #3B82F6'
                            }}
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div style={{
                            width: 'clamp(60px, 10vw, 80px)',
                            height: 'clamp(60px, 10vw, 80px)',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #DBEAFE 0%, #3B82F6 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            border: '3px solid #3B82F6'
                          }}>
                            <Users size={32} style={{ color: '#2563EB' }} />
                          </div>
                        )}
                        <div style={{ width: '100%' }}>
                          <div style={{ 
                            fontSize: 'clamp(1rem, 2vw, 1.25rem)', 
                            fontWeight: 'bold', 
                            color: '#1F2937', 
                            marginBottom: '0.25rem',
                            lineHeight: 1.2
                          }}>
                            {fixture.awayTeam}
                          </div>
                          {score && (
                            <div style={{ 
                              fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', 
                              color: fixture.status === 'completed' ? '#059669' : '#3B82F6',
                              fontWeight: '600'
                            }}>
                              {fixture.status === 'completed' ? 'Final: ' : 'Sets: '}{score.away}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Pairing Status */}
                    {fixture.selectedPlayers && (
                      <div style={{
                        background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                        border: '1px solid #10B981',
                        borderRadius: '0.5rem',
                        padding: '0.75rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <Users size={16} style={{ color: '#10B981' }} />
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#065F46' }}>
                          Pairings Set: {fixture.selectedPlayers.home.player1?.name} & {fixture.selectedPlayers.home.player2?.name} vs {fixture.selectedPlayers.away.player1?.name} & {fixture.selectedPlayers.away.player2?.name}
                        </span>
                      </div>
                    )}

                    {/* Date & Time */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '1.5rem',
                      padding: '0.75rem',
                      background: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)',
                      borderRadius: '0.5rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6B7280', fontSize: '0.875rem', fontWeight: '600' }}>
                        <Calendar size={16} />
                        <span>{new Date(fixture.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6B7280', fontSize: '0.875rem', fontWeight: '600' }}>
                        <Clock size={16} />
                        <span>{fixture.time}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: 'clamp(0.5rem, 1vw, 0.75rem)'
                    }}>
                      {fixture.status === 'scheduled' && (
                        <>
                          {!isScoreKeeper() && (
                            <button
                              onClick={() => handleSetPairings(fixture)}
                              className="btn btn-secondary"
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                gap: '0.5rem',
                                fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                                padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(0.75rem, 2vw, 1rem)',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <Users size={16} />
                              <span style={{ display: window.innerWidth < 900 ? 'none' : 'inline' }}>Set </span>Pairings
                            </button>
                          )}
                        <button
                            onClick={() => {
                              if (fixture.selectedPlayers) {
                                startMatchWithPreSetPairings(fixture);
                              } else {
                                openPlayerSelection(fixture);
                              }
                            }}
                          className="btn btn-primary"
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              gap: '0.5rem',
                              fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                              padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(0.75rem, 2vw, 1rem)',
                              whiteSpace: 'nowrap'
                            }}
                        >
                          <Play size={16} />
                            {fixture.selectedPlayers ? 'Start' : 'Select Players'}
                        </button>
                        </>
                      )}
                      {fixture.status === 'live' && (
                        <>
                          <Link
                            to={`/match/${fixture.id}/scoring`}
                            className="btn btn-primary"
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              gap: '0.5rem', 
                              textDecoration: 'none',
                              fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                              padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(0.75rem, 2vw, 1rem)',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Target size={16} />
                            Live Scoring
                          </Link>
                          <Link
                            to={`/fan/match/${fixture.id}`}
                          className="btn btn-secondary"
                            target="_blank"
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              gap: '0.5rem', 
                              textDecoration: 'none',
                              fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                              padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(0.75rem, 2vw, 1rem)',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Eye size={16} />
                            Fan View
                          </Link>
                        </>
                      )}
                      {fixture.status === 'completed' && (
                        <Link
                          to={`/match/${fixture.id}`}
                          className="btn btn-secondary"
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: '0.5rem', 
                            textDecoration: 'none',
                            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                            padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(0.75rem, 2vw, 1rem)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <BarChart3 size={16} />
                          Results
                        </Link>
                      )}
                      
                      {/* Management Button - Only visible to Admins */}
                      {!isScoreKeeper() && (
                        <button
                          onClick={() => openFixtureManager(fixture)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(0.75rem, 2vw, 1rem)',
                            background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                            whiteSpace: 'nowrap',
                            minWidth: '120px'
                          }}
                        >
                          <Settings size={16} />
                          Manage
                        </button>
                      )}
                  </div>
                </div>
              </motion.div>
              );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <Calendar size={64} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No Fixtures Found</h3>
            <p className="text-gray-600 mb-6">
              {filter === 'all' 
                ? 'No fixtures have been created yet'
                : `No ${filter} fixtures found`
              }
            </p>
            <Link
              to="/fixtures/generate"
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <Plus size={20} />
              Generate Fixtures
            </Link>
          </motion.div>
        )}

        {/* Player Selection Modal */}
        {showPlayerSelection && selectedFixture && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}
            onClick={() => setShowPlayerSelection(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                padding: '2rem',
                maxWidth: '900px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }}
            >
              <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1C563D' }}>
                Select Players for Match
              </h2>
              <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
                {selectedFixture.homeTeam} vs {selectedFixture.awayTeam}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Home Team Players */}
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: '#10B981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Shield size={20} />
                    {selectedFixture.homeTeam}
                  </h3>
                  
                  {['player1', 'player2'].map((playerKey, idx) => {
                    const homeTeam = teams[selectedFixture.homeTeamId];
                    const teamPlayers = homeTeam?.players || [];
                    
                    return (
                      <div key={playerKey} style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                          Player {idx + 1}
                        </label>
                        <select
                          value={selectedPlayers.home[playerKey]?.id || ''}
                          onChange={(e) => {
                            const player = teamPlayers.find(p => p.id === e.target.value);
                            setSelectedPlayers(prev => ({
                              ...prev,
                              home: { ...prev.home, [playerKey]: player }
                            }));
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '2px solid #E5E7EB',
                            borderRadius: '0.5rem',
                            fontSize: '0.875rem',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">Select player...</option>
                          {teamPlayers.map(player => (
                            <option key={player.id} value={player.id}>
                              {player.name} ({player.category})
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>

                {/* Away Team Players */}
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: '#3B82F6', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Shield size={20} />
                    {selectedFixture.awayTeam}
                  </h3>
                  
                  {['player1', 'player2'].map((playerKey, idx) => {
                    const awayTeam = teams[selectedFixture.awayTeamId];
                    const teamPlayers = awayTeam?.players || [];
                    
                    return (
                      <div key={playerKey} style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                          Player {idx + 1}
                        </label>
                        <select
                          value={selectedPlayers.away[playerKey]?.id || ''}
                          onChange={(e) => {
                            const player = teamPlayers.find(p => p.id === e.target.value);
                            setSelectedPlayers(prev => ({
                              ...prev,
                              away: { ...prev.away, [playerKey]: player }
                            }));
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '2px solid #E5E7EB',
                            borderRadius: '0.5rem',
                            fontSize: '0.875rem',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">Select player...</option>
                          {teamPlayers.map(player => (
                            <option key={player.id} value={player.id}>
                              {player.name} ({player.category})
                            </option>
                          ))}
                        </select>
              </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Modal Actions */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowPlayerSelection(false)}
                  className="btn btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  onClick={startMatchWithPlayers}
                  className="btn btn-primary"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  disabled={!selectedPlayers.home.player1 || !selectedPlayers.home.player2 || !selectedPlayers.away.player1 || !selectedPlayers.away.player2}
                >
                  <Play size={16} />
                  Start Match
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Fixture Manager Modal */}
      {showFixtureManager && managerFixture && (
        <FixtureManager
          fixture={managerFixture}
          onUpdate={handleFixtureUpdate}
          onClose={closeFixtureManager}
        />
      )}

      {/* Team Pairings Modal */}
      {showTeamPairings && pairingsFixture && (
        <TeamPairings
          fixture={pairingsFixture}
          onSave={handleSavePairings}
          onClose={() => {
            setShowTeamPairings(false);
            setPairingsFixture(null);
          }}
        />
      )}

      {/* Match Setup Modal */}
      {showMatchSetup && setupFixture && (
        <MatchSetup
          fixture={setupFixture}
          onSave={handleSaveMatchSetup}
          onClose={() => {
            setShowMatchSetup(false);
            setSetupFixture(null);
          }}
        />
      )}

      {/* Reschedule Group Modal */}
      {showRescheduleModal && rescheduleGroup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem'
          }}
          onClick={() => {
            setShowRescheduleModal(false);
            setRescheduleGroup(null);
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
              borderRadius: '1.5rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(28, 86, 61, 0.1)'
            }}
          >
            {/* Modal Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1.5rem' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                  borderRadius: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Calendar size={20} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold', 
                    color: '#1C563D',
                    marginBottom: '0.25rem'
                  }}>
                    Reschedule Fixtures
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                    {rescheduleGroup.homeTeam} vs {rescheduleGroup.awayTeam}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowRescheduleModal(false);
                  setRescheduleGroup(null);
                }}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '0.5rem',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#F3F4F6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <X size={20} style={{ color: '#6B7280' }} />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{
                background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)',
                padding: '1rem',
                borderRadius: '1rem',
                border: '1px solid #BBF7D0',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontSize: '0.875rem', color: '#065F46', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Fixture Details:
                </div>
                <div style={{ fontSize: '0.875rem', color: '#1C563D' }}>
                  • {rescheduleGroup.matches.length} matches to reschedule<br/>
                  • Current date: {new Date(rescheduleGroup.date).toLocaleDateString()}<br/>
                  • Court: {rescheduleGroup.court}<br/>
                  • Round: {rescheduleGroup.round}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.875rem', 
                  fontWeight: '600', 
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  New Date:
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '2px solid #E5E7EB',
                    borderRadius: '0.75rem',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1C563D';
                    e.target.style.boxShadow = '0 0 0 3px rgba(28, 86, 61, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.875rem', 
                  fontWeight: '600', 
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Round Number:
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={newRound}
                  onChange={(e) => setNewRound(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '2px solid #E5E7EB',
                    borderRadius: '0.75rem',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1C563D';
                    e.target.style.boxShadow = '0 0 0 3px rgba(28, 86, 61, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => {
                  setShowRescheduleModal(false);
                  setRescheduleGroup(null);
                }}
                style={{
                  flex: 1,
                  padding: '0.875rem 1.5rem',
                  background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
                  color: '#1C563D',
                  border: '2px solid #E2E8F0',
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#CBD5E1';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E2E8F0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRescheduleGroup}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.875rem 1.5rem',
                  background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 6px -1px rgba(28, 86, 61, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(28, 86, 61, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(28, 86, 61, 0.3)';
                }}
              >
                <Calendar size={18} />
                Reschedule {rescheduleGroup.matches.length} Matches
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default FixturesPage;
